import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { Menu, Button } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppDispatch } from 'redux/store';
import ExpenseTransactionTable, { DEFAULT_VISIBLE_COLUMN_KEYS, VISIBLE_COLUMN_OPTIONS } from '../Table';
import SummaryTable from '../SummaryTable';
import ExpenseTransactionSearchAndFilter from '../SearchAndFilter';
import BatchEditModal from '../BatchEditModal';
import {
    getTransactionsAction,
    syncTransactionsAction,
    batchEditTransactionsAction,
} from 'redux/action/transactionAction';
import { getSummaryAction } from 'redux/action/summaryAction';
import { resetTransactions, setSyncLoading } from 'redux/slice/transactionSlice';
import { resetSummary } from 'redux/slice/summarySlice';
import { updateOneOffConfigurations } from 'redux/slice/oneOffSlice';
import { resetChartOfAccounts } from 'redux/slice/chartOfAccountSlice';
import { resetCustomerOptions } from 'redux/slice/customerSlice';
import { resetClassOptions } from 'redux/slice/classSlice';
import { resetLocationOptions } from 'redux/slice/locationSlice';
import { resetVendors } from 'redux/slice/vendorSlice';
import {
    chartOfAccountAction,
} from 'redux/action/chartOfAccountAction';
import {
    customerOptionsAction,
} from 'redux/action/customerAction';
import {
    classOptionsAction,
} from 'redux/action/classAction';
import {
    locationOptionsAction,
} from 'redux/action/locationAction';
import { vendorAction } from 'redux/action/vendorAction';
import { getColumnVisibilityAction, saveColumnVisibilityAction } from 'redux/action/companyAction';
// import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import styles from './index.module.scss';
import { companyAbortController } from 'utils/abortController';
import { getOptionLabel, checkPermission, toastText } from 'utils/utils';

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// const TIMEZONE = process.env.REACT_APP_TIMEZONE || 'America/New_York';
import DownloadAttachmentModal from '../DownloadAttachmentModal';
import HistoryModal from '../HistoryModal';
export interface ExpenseTransaction {
    // Transaction fields
    id: string;                    // Transaction database ID
    txnId: string;                 // QuickBooks transaction ID
    txnDate: string;
    vendorId: string;
    vendorName: string;
    paymentType?: string;
    docNumber?: string;
    syncToken: string;
    locationId?: string;
    locationName?: string;
    memo?: string;
    totalAmt: number;
    lastUpdatedTime: string;
    attachable: boolean;
    isDeletedFromQbo: boolean;
    qboObject: any;
    entityType: string;            // "Purchase" | "Bill" | "VendorCredit" | "JournalEntry" | "Deposit"

    // ExpenseLine fields (flattened)
    accountId: string;
    accountName: string;
    qboLineId: string;
    customerId?: string;
    customerName?: string;
    classId?: string;
    className?: string;
    description?: string;
    amount: number;

    // Timestamps
    createdAt: string;
    updatedAt: string;
}

interface TreeSelectOption {
    value: string;
    title: string;
    isProject?: boolean;
    children?: TreeSelectOption[];
}

// Transform Redux data to TreeSelect format
const transformToTreeData = (data: any[]): TreeSelectOption[] => {
    if (!Array.isArray(data)) {
        return [];
    }

    return data
        .filter((item: any) => item && (item.Id || item.id))
        .map((item: any) => ({
            value: item.Id || item.id,
            title: item.title || item.Name || item.DisplayName || item.FullyQualifiedName || item.label || 'Unknown',
            isProject: item.isProject === true,
            children: item.children && Array.isArray(item.children) && item.children.length > 0
                ? transformToTreeData(item.children)
                : [],
        }));
};

// Look up isProject flag from tree-structured customer options
const getOptionIsProject = (value: string, options: TreeSelectOption[]): boolean => {
    for (const node of options) {
        if (node.value === value) return node.isProject === true;
        if (node.children && node.children.length > 0) {
            const found = getOptionIsProject(value, node.children);
            if (found) return true;
        }
    }
    return false;
};

// Helper function to get previous month date range
// const getPreviousMonthRange = (): [Dayjs, Dayjs] => {
//     const today = dayjs().tz(TIMEZONE);
//     const firstDayOfPreviousMonth = today.subtract(1, 'month').startOf('month');
//     const lastDayOfPreviousMonth = today.subtract(1, 'month').endOf('month');
//     return [firstDayOfPreviousMonth, lastDayOfPreviousMonth];
// };

// Helper utility to group selected rows by txnId (ignore no-line entries)
const groupSelectedRowsByTxnId = (selectedRowKeys: string[]): { txnId: string; lineIds: string[]; entityType: string }[] => {
    const grouped = selectedRowKeys.reduce((acc, rowKey) => {
        const parts = rowKey.split(':');
        const [txnId, qboLineId, entityType] = parts;

        // Skip if qboLineId is 'no-line' or empty
        if (!qboLineId || qboLineId === 'no-line') {
            return acc;
        }

        const existingGroup = acc.find(group => group.txnId === txnId && group.entityType === entityType);
        if (existingGroup) {
            existingGroup.lineIds.push(qboLineId);
        } else {
            acc.push({
                txnId: txnId,
                lineIds: [qboLineId],
                entityType: entityType
            });
        }
        return acc;
    }, [] as { txnId: string; lineIds: string[]; entityType: string }[]);

    return grouped;
};

const getSelectedItemBasedCount = (
    rows: ExpenseTransaction[],
    selectedRowKeys: string[]
): number => {
    if (!rows?.length || !selectedRowKeys?.length) return 0;
    const selectedSet = new Set(selectedRowKeys);

    return rows.reduce((count, record) => {
        const rowKey = `${record.txnId}:${record.qboLineId || 'no-line'}:${record.entityType}`;
        if (selectedSet.has(rowKey) && (record as any).lineBasedOn === 'Item') {
            return count + 1;
        }
        return count;
    }, 0);
};

interface ExpenseTransactionMainProps {
    propSelectedExpenseGroup: string;
    propOnExpenseGroupChange: (expenseGroupId: string) => void;
    propExpenseGroupOptions: Array<{ value: string; label: string }>;
    propExpenseGroupsLoading: boolean;
    propDateRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
    propSetDateRange: (range: [dayjs.Dayjs, dayjs.Dayjs] | null) => void;
}

const ExpenseTransactionMain: React.FC<ExpenseTransactionMainProps> = ({
    propSelectedExpenseGroup: propSelectedExpenseGroup,
    propOnExpenseGroupChange: propOnExpenseGroupChange,
    propExpenseGroupOptions: propExpenseGroupOptions,
    propExpenseGroupsLoading: propExpenseGroupsLoading,
    propDateRange: propDateRange,
    propSetDateRange: propSetDateRange
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Redux state selectors
    const { selectedCompanyDetails } = useSelector((state: any) => state.companies);
    const selectedCompanyId =
        selectedCompanyDetails?.company?.id ??
        selectedCompanyDetails?.id ??
        localStorage.getItem('companyId') ??
        'global';
    const { data: qbChartOfAccounts } = useSelector((state: any) => state.chartOfAccounts);
    const { optionData: qbCustomers } = useSelector((state: any) => state.customer);
    const { optionData: qbClass } = useSelector((state: any) => state.class);
    const { optionData: qbLocations } = useSelector((state: any) => state.locations);
    const { data: qbVendors } = useSelector((state: any) => state.vendors);
    const { data: expenseGroups } = useSelector((state: any) => state.expenseGroups);
    const selectedCompanyPermission = useSelector(
        (state: any) => state?.companies?.selectedCompanyDetails?.role?.permissions
    );
    const {
        transactions,
        pagination,
        grandTotal: transactionGrandTotal,
        isLoading: transactionsLoading,
        isLoadingMore,
        hasMore,
        fistTimeFetchLoading: transactionsFirstTimeLoading,
        batchEdit,
        syncLoading
    } = useSelector((state: any) => state.transactions);
    const {
        summary,
        grandTotal,
        pagination: summaryPagination,
        isLoading: summaryLoading,
        isLoadingMore: summaryIsLoadingMore,
        hasMore: summaryHasMore,
        fistTimeFetchLoading: summaryFirstTimeLoading
    } = useSelector((state: any) => state.summary);
    const { oneOffConfigurations } = useSelector((state: any) => state.oneOff);

    const [data, setData] = useState<ExpenseTransaction[]>([]);
    const [isSummaryView, setIsSummaryView] = useState(false);
    const [loading, setLoading] = useState(false);
    const [switchLoading, setSwitchLoading] = useState(false);
    const [lastSyncTime, setLastSyncTime] = useState<Date>(new Date());
    const [batchEditModalVisible, setBatchEditModalVisible] = useState(false);
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [historyModalVisible, setHistoryModalVisible] = useState(false);


    // Checkbox state management
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [isHeaderChecked, setIsHeaderChecked] = useState(false);
    const [isPaginationLoad, setIsPaginationLoad] = useState(false);
    const [isSelectAllMode, setIsSelectAllMode] = useState(false);
    const previousDataLengthRef = useRef(0);

    // Extract unique txnIds from selectedRowKeys
    const selectedTxnIds = useMemo(() => {
        const txnIds = new Set<string>();
        selectedRowKeys.forEach((rowKey) => {
            const [txnId] = rowKey.split(':');
            if (txnId) {
                txnIds.add(txnId);
            }
        });
        return Array.from(txnIds);
    }, [selectedRowKeys]);

    // Filter states
    // Use shared date range from parent component
    const dateRange = propDateRange;
    const setDateRange = propSetDateRange;
    const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
    const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
    const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
    const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
    const [selectedEntityTypes, setSelectedEntityTypes] = useState<string[]>([]);
    const [selectedLineBasedOn, setSelectedLineBasedOn] = useState<string[]>([]);
    const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
    const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(DEFAULT_VISIBLE_COLUMN_KEYS);

    const isOneOffMode = propSelectedExpenseGroup === 'all';

    const validColumnKeysSet = useMemo(
        () => new Set(VISIBLE_COLUMN_OPTIONS.map((o) => o.key)),
        []
    );

    // Fetch column visibility from backend when company is set (empty response = all columns checked)
    useEffect(() => {
        if (!selectedCompanyId) return;
        const controller = new AbortController();
        let cancelled = false;
        dispatch(
            getColumnVisibilityAction({
                companyId: selectedCompanyId,
                signal: controller.signal,
            })
        )
            .unwrap()
            .then((data) => {
                if (cancelled) return;
                const raw =
                    data?.data?.columnVisibility ??
                    data?.columnVisibility ??
                    data?.data ??
                    data;
                const arr = Array.isArray(raw) ? raw : [];
                if (arr.length === 0) {
                    setVisibleColumnKeys(DEFAULT_VISIBLE_COLUMN_KEYS);
                    return;
                }
                const filtered = arr.filter(
                    (k: unknown) => typeof k === 'string' && validColumnKeysSet.has(k)
                ) as string[];
                setVisibleColumnKeys(
                    filtered.length > 0 ? filtered : DEFAULT_VISIBLE_COLUMN_KEYS
                );
            })
            .catch(() => {
                if (!cancelled) setVisibleColumnKeys(DEFAULT_VISIBLE_COLUMN_KEYS);
            });
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [selectedCompanyId, validColumnKeysSet, dispatch]);

    const onVisibleColumnsChange = useCallback((keys: string[]) => {
        setVisibleColumnKeys(keys);
    }, []);

    const onSaveVisibleColumnsAsDefault = useCallback(async () => {
        if (!selectedCompanyId) return;
        try {
            const res = await dispatch(
                saveColumnVisibilityAction({
                    companyId: selectedCompanyId,
                    columnVisibility: visibleColumnKeys,
                })
            ).unwrap();
            const message =
                res?.message ??
                res?.data?.message ??
                'Visible columns saved as default.';
            toastText(message, 'success');
        } catch (err: unknown) {
            const msg =
                err && typeof err === 'object' && 'payload' in err
                    ? (err as { payload?: { message?: string } })?.payload?.message
                    : err && typeof err === 'object' && 'response' in err
                        ? (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                        : undefined;
            toastText(msg ?? 'Failed to save visible columns.', 'error');
        }
    }, [selectedCompanyId, visibleColumnKeys, dispatch]);

    const clearEntityLineFilters = useCallback(() => {
        setSelectedEntityTypes([]);
        setSelectedLineBasedOn([]);
        // Vendors are maintained with accounts/customers/classes/locations per expense group; do not clear here
    }, []);

    // Rehydrate filters from Redux when oneOffConfigurations exists and has filter data
    // Use useLayoutEffect to ensure it runs before save effect


    // Save filters to Redux whenever they change in One-Off mode

    // Use propSelectedExpenseGroup from props instead of local state
    const [currentSort, setCurrentSort] = useState<{ sortBy: string; sortOrder: 'asc' | 'desc' }>({
        sortBy: 'txnDate',
        sortOrder: 'desc'
    });
    const [currentSummarySort, setCurrentSummarySort] = useState<{ sortBy: string; sortOrder: 'asc' | 'desc' }>({
        sortBy: 'totalAmount',
        sortOrder: 'desc'
    });

    // Transform Redux data to TreeSelect format
    const accountOptions = transformToTreeData(qbChartOfAccounts || []);
    const customerOptions = transformToTreeData(qbCustomers || []);
    const classOptions = transformToTreeData(qbClass || []);
    const locationOptions = transformToTreeData(qbLocations || []);
    const vendorOptions = transformToTreeData(qbVendors || []);


    const isExpenseGroupsAddPermission = checkPermission(selectedCompanyPermission, {
        permissionName: 'Expense Groups',
        permission: ['add'],
    });


    // Summary data from Redux
    const summaryData = summary || [];

    // Fetch transactions function
    const fetchTransactions = useCallback(async (page = 1, sortBy = currentSort.sortBy, sortOrder = currentSort.sortOrder) => {
        const requestKey = 'fetchTransactions';
        const { controller, requestId } =
            companyAbortController.createController(requestKey);

        // Only set loading if this is the latest request
        if (companyAbortController.isLatestRequest(requestKey, requestId)) {
            setLoading(true);
        }

        const params = {
            fromDate: dateRange?.[0]?.format('YYYY-MM-DD'),
            toDate: dateRange?.[1]?.format('YYYY-MM-DD'),
            accountIds: selectedAccounts || [],
            customerIds: selectedCustomers || [],
            classIds: selectedClasses || [],
            locationIds: selectedLocations || [],
            entityTypes: selectedEntityTypes || [],
            lineBasedOn: selectedLineBasedOn || [],
            vendorIds: selectedVendors || [],
            sortBy: sortBy || 'txnDate',
            sortOrder: sortOrder || 'desc',
            page: page || 1,
            limit: 20,
            signal: controller.signal,
        };

        try {

            await dispatch(getTransactionsAction(params));

            companyAbortController.cleanupRequest(requestKey);
        }
        catch (error: any) {
            if (error.name === 'AbortError') {
                // Request was aborted, do nothing
                console.log('Request aborted, new request in progress...');
                return;
            }

            console.error('API Error:', error);
            setLoading(false);
        } finally {

            if (companyAbortController.isLatestRequest(requestKey, requestId)) {
                setLoading(false);
            }
            companyAbortController.cleanupRequest(requestKey);
        }

    }, [dateRange, selectedAccounts, selectedCustomers, selectedClasses, selectedLocations, selectedEntityTypes, selectedLineBasedOn, selectedVendors, currentSort.sortBy, currentSort.sortOrder, dispatch]);

    // Fetch summary function
    const fetchSummary = useCallback(async (page = 1, sortBy = currentSummarySort.sortBy, sortOrder = currentSummarySort.sortOrder) => {
        const params = {
            fromDate: dateRange?.[0]?.format('YYYY-MM-DD'),
            toDate: dateRange?.[1]?.format('YYYY-MM-DD'),
            accountIds: selectedAccounts,
            customerIds: selectedCustomers,
            classIds: selectedClasses,
            locationIds: selectedLocations,
            vendorIds: selectedVendors,
            sortBy,
            sortOrder,
            page,
            limit: 50,
        };

        dispatch(getSummaryAction(params));
    }, [dateRange, selectedAccounts, selectedCustomers, selectedClasses, selectedLocations, selectedVendors, currentSummarySort.sortBy, currentSummarySort.sortOrder, dispatch]);

    // Load more transactions for infinite scroll
    const handleLoadMore = () => {
        if (hasMore && !isLoadingMore && !transactionsLoading) {
            setIsPaginationLoad(true);
            const nextPage = pagination.currentPage + 1;
            fetchTransactions(nextPage, currentSort.sortBy, currentSort.sortOrder);
        }
    };

    // Load more summary for infinite scroll
    const handleLoadMoreSummary = () => {
        if (summaryHasMore && !summaryIsLoadingMore && !summaryLoading) {
            const nextPage = summaryPagination.currentPage + 1;
            fetchSummary(nextPage, currentSummarySort.sortBy, currentSummarySort.sortOrder);
        }
    };

    // Handle sorting for transactions
    const handleSort = (sortBy: string, sortOrder: 'asc' | 'desc') => {
        setCurrentSort({ sortBy, sortOrder });
        // Reset transaction data before fetching new sorted data
        dispatch(resetTransactions());
        fetchTransactions(1, sortBy, sortOrder);
    };

    // Handle sorting for summary
    const handleSummarySort = (sortBy: string, sortOrder: 'asc' | 'desc') => {
        setCurrentSummarySort({ sortBy, sortOrder });
        // Reset summary data before fetching new sorted data
        dispatch(resetSummary());
        fetchSummary(1, sortBy, sortOrder);
    };


    // Handle company changes - reload all data when company changes
    useEffect(() => {
        if (selectedCompanyDetails) {
            // Reset all filter states when company changes
            setSelectedAccounts([]);
            setSelectedCustomers([]);
            setSelectedClasses([]);
            setSelectedLocations([]);
            setSelectedEntityTypes([]);
            setSelectedLineBasedOn([]);
            setSelectedVendors([]);
            // Expense group is managed by parent component
            setData([]);

            // Reset Redux state
            dispatch(resetTransactions());
            dispatch(resetSummary());

            // Reset dropdown data before fetching new company data
            dispatch(resetChartOfAccounts());
            dispatch(resetCustomerOptions());
            dispatch(resetClassOptions());
            dispatch(resetLocationOptions());
            dispatch(resetVendors());

            // Reload all data for the new company
            dispatch(chartOfAccountAction());
            dispatch(customerOptionsAction());
            dispatch(classOptionsAction());
            dispatch(locationOptionsAction());
            dispatch(vendorAction());
            // Expense groups are now fetched by parent component
        }
    }, [selectedCompanyDetails, dispatch]);

    useEffect(() => {
        const expenseGroupId = searchParams.get('expenseGroup');
        if (expenseGroupId && expenseGroups && expenseGroups.length > 0) {
            // Check if the expense group exists
            const expenseGroup = expenseGroups.find(
                (group: any) => group.id === expenseGroupId
            );
            if (expenseGroup) {
                handleExpenseGroupChange(expenseGroupId);
                // Clear the URL parameter after processing to avoid re-triggering
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('expenseGroup');
                navigate(`/expense-allocations?${newSearchParams.toString()}`, {
                    replace: true,
                });
            }
        }
    }, [searchParams, expenseGroups, navigate]);
    useEffect(() => {
        const openHistoryModal = searchParams.get('openHistoryModal');
        // const historyFilter = searchParams.get('historyFilter');

        if (openHistoryModal === 'true') {
            setHistoryModalVisible(true);
            // Clear the URL parameters after opening the modal
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('openHistoryModal');
            newSearchParams.delete('historyFilter');
            navigate(`/expense-allocations?${newSearchParams.toString()}`, {
                replace: true,
            });
        }
    }, [searchParams, navigate]);

    // Update data when transactions change
    useEffect(() => {
        if (transactions) {
            setData(transactions);
        }
    }, [transactions]);

    // Handle header checkbox change
    const handleHeaderCheckboxChange = (checked: boolean) => {
        setIsHeaderChecked(checked);
        setIsSelectAllMode(checked);
        if (checked) {
            // Select all visible rows
            const allVisibleKeys = data.map(item => `${item.txnId}:${item.qboLineId || 'no-line'}:${item.entityType}`);
            setSelectedRowKeys(allVisibleKeys);
        } else {
            // Deselect all rows
            setSelectedRowKeys([]);
        }
    };

    // Handle individual row checkbox change
    const handleRowCheckboxChange = (rowKey: string, checked: boolean) => {
        if (checked) {
            setSelectedRowKeys(prev => [...prev, rowKey]);
        } else {
            setSelectedRowKeys(prev => prev.filter(key => key !== rowKey));
            // If we uncheck a row while in select all mode, exit select all mode
            if (isSelectAllMode) {
                setIsSelectAllMode(false);
            }
        }
    };

    // Handle data changes and maintain selection in select all mode
    useLayoutEffect(() => {
        const currentDataLength = data.length;
        const previousDataLength = previousDataLengthRef.current;

        // Check if new data was added (pagination load)
        if (currentDataLength > previousDataLength && isSelectAllMode) {
            // New data was loaded, select all records immediately
            const allVisibleKeys = data.map(item => `${item.txnId}:${item.qboLineId || 'no-line'}:${item.entityType}`);
            setSelectedRowKeys(allVisibleKeys);
        }

        // Update the ref with current length
        previousDataLengthRef.current = currentDataLength;

        // Reset pagination flag if it was set
        if (isPaginationLoad) {
            setIsPaginationLoad(false);
        }
    }, [data, isSelectAllMode, isPaginationLoad]);

    // Update header checkbox state when selected rows change (but not during pagination load)
    useEffect(() => {
        if (data.length === 0) {
            setIsHeaderChecked(false);
            setIsSelectAllMode(false);
            return;
        }

        // Skip header checkbox update during pagination load to prevent race condition
        if (isPaginationLoad) {
            return;
        }

        const allVisibleKeys = data.map(item => `${item.txnId}:${item.qboLineId || 'no-line'}:${item.entityType}`);
        const allSelected = allVisibleKeys.every(key => selectedRowKeys.includes(key));

        // Only update header checkbox if we're not in select all mode
        if (!isSelectAllMode) {
            setIsHeaderChecked(allSelected);
        }
    }, [selectedRowKeys, data, isPaginationLoad, isSelectAllMode]);

    // Reset checkbox state when filters change
    useEffect(() => {
        setSelectedRowKeys([]);
        setIsHeaderChecked(false);
        setIsSelectAllMode(false);
        setIsPaginationLoad(false);
    }, [dateRange, selectedAccounts, selectedCustomers, selectedClasses, selectedLocations, selectedEntityTypes, selectedLineBasedOn, selectedVendors, propSelectedExpenseGroup]);

    // Handle switch between detailed and summary views with loading
    const handleViewSwitch = async (newIsSummaryView: boolean) => {
        if (newIsSummaryView === isSummaryView) return; // No change needed

        setSwitchLoading(true);

        // Clear selected transactions and reset UI state when switching views
        setData(prevData => prevData.map(item => ({ ...item, selected: false })));
        setSelectedRowKeys([]);
        setIsHeaderChecked(false);
        setIsPaginationLoad(false);

        try {
            if (newIsSummaryView) {
                // Switching to summary view
                clearEntityLineFilters();
                dispatch(resetSummary());
                await dispatch(getSummaryAction({
                    fromDate: dateRange?.[0]?.format('YYYY-MM-DD'),
                    toDate: dateRange?.[1]?.format('YYYY-MM-DD'),
                    accountIds: selectedAccounts,
                    customerIds: selectedCustomers,
                    classIds: selectedClasses,
                    locationIds: selectedLocations,
                    vendorIds: selectedVendors,
                    sortBy: currentSummarySort.sortBy,
                    sortOrder: currentSummarySort.sortOrder,
                    page: 1,
                    limit: 50,
                }));
            } else {
                // Switching to detailed view
                dispatch(resetTransactions());
                await dispatch(getTransactionsAction({
                    fromDate: dateRange?.[0]?.format('YYYY-MM-DD'),
                    toDate: dateRange?.[1]?.format('YYYY-MM-DD'),
                    accountIds: selectedAccounts,
                    customerIds: selectedCustomers,
                    classIds: selectedClasses,
                    locationIds: selectedLocations,
                    entityTypes: [],
                    lineBasedOn: [],
                    vendorIds: selectedVendors,
                    sortBy: currentSort.sortBy,
                    sortOrder: currentSort.sortOrder,
                    page: 1,
                    limit: 20,
                }));
            }

            // Only switch the view after data is loaded
            setIsSummaryView(newIsSummaryView);
        } catch (error) {
            console.error('Error switching view:', error);
        } finally {
            setSwitchLoading(false);
        }
    };

    // An AI-chat-confirmed expense-line edit patches the edited row directly in
    // the transactions store, but SummaryTable's totals come from a separate
    // fetch and would otherwise stay stale until the user touched a filter.
    // Same guard order as the other consumers of this notice: scope, company.
    const { dataChanged } = useSelector((state: any) => state?.chatBubble);
    useEffect(() => {
        if (!dataChanged || dataChanged.scope !== 'expenseAllocation') {
            return;
        }
        if (dataChanged.companyId !== localStorage.getItem('companyId')) {
            return;
        }
        if (isSummaryView) {
            dispatch(resetSummary());
            fetchSummary();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataChanged]);

    // Handle filter changes for summary view
    useEffect(() => {
        if (isSummaryView) {
            dispatch(resetSummary());
            fetchSummary();
        }
    }, [
        dateRange,
        selectedAccounts,
        selectedCustomers,
        selectedClasses,
        selectedLocations,
        selectedVendors,
        propSelectedExpenseGroup,
    ]);

    // Listen for notification-triggered data fetch
    useEffect(() => {
        const handleTriggerDataFetch = (event: CustomEvent) => {
            const { dateRange: newDateRange } = event.detail;

            if (newDateRange && Array.isArray(newDateRange) && newDateRange.length === 2) {
                // Validate that the dates are dayjs objects and cast to proper type
                const [startDate, endDate] = newDateRange;
                if (dayjs.isDayjs(startDate) && dayjs.isDayjs(endDate)) {
                    const typedDateRange: [dayjs.Dayjs, dayjs.Dayjs] = [startDate, endDate];

                    // Update the date range prop if it's different
                    if (propDateRange && propSetDateRange) {
                        const currentDateRange = propDateRange;
                        const isDateRangeDifferent = !currentDateRange ||
                            !currentDateRange[0]?.isSame(typedDateRange[0], 'day') ||
                            !currentDateRange[1]?.isSame(typedDateRange[1], 'day');

                        if (isDateRangeDifferent) {
                            propSetDateRange(typedDateRange);
                        }
                    }

                    // Trigger data fetch based on current view
                    if (isSummaryView) {
                        dispatch(resetSummary());
                        dispatch(getSummaryAction({
                            fromDate: typedDateRange[0].format('YYYY-MM-DD'),
                            toDate: typedDateRange[1].format('YYYY-MM-DD'),
                            accountIds: selectedAccounts,
                            customerIds: selectedCustomers,
                            classIds: selectedClasses,
                            locationIds: selectedLocations,
                            vendorIds: selectedVendors,
                            sortBy: currentSummarySort.sortBy,
                            sortOrder: currentSummarySort.sortOrder,
                            page: 1,
                            limit: 50,
                        }));
                    } else {
                        dispatch(resetTransactions());
                        dispatch(getTransactionsAction({
                            fromDate: typedDateRange[0].format('YYYY-MM-DD'),
                            toDate: typedDateRange[1].format('YYYY-MM-DD'),
                            accountIds: selectedAccounts,
                            customerIds: selectedCustomers,
                            classIds: selectedClasses,
                            locationIds: selectedLocations,
                            entityTypes: selectedEntityTypes,
                            lineBasedOn: selectedLineBasedOn,
                            vendorIds: selectedVendors,
                            sortBy: currentSort.sortBy,
                            sortOrder: currentSort.sortOrder,
                            page: 1,
                            limit: 20,
                        }));
                    }
                }
            }
        };

        // Add event listener
        window.addEventListener('triggerDataFetch', handleTriggerDataFetch as unknown as EventListener);

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('triggerDataFetch', handleTriggerDataFetch as unknown as EventListener);
        };
    }, [isSummaryView, selectedAccounts, selectedCustomers, selectedClasses, selectedLocations, selectedEntityTypes, selectedLineBasedOn, selectedVendors, currentSort, currentSummarySort, dispatch, propDateRange, propSetDateRange]);

    const handleDownloadModalClose = () => {
        setDownloadModalVisible(false);
    };


    // Handle sync
    const handleSync = async (fromDate?: string, toDate?: string) => {
        try {
            // Use provided dates from SyncDateRangeDropdown if available
            if (fromDate && toDate) {
                // Set sync loading to true at the start
                dispatch(setSyncLoading(true));

                try {
                    // First, call all option actions and wait for them to complete
                    await Promise.all([
                        dispatch(chartOfAccountAction()),
                        dispatch(customerOptionsAction()),
                        dispatch(classOptionsAction()),
                        dispatch(locationOptionsAction()),
                        dispatch(vendorAction()),
                    ]);

                    // After all option actions complete, call sync transactions action
                    // Note: syncTransactionsAction will manage syncLoading state through its pending/fulfilled/rejected states
                    await dispatch(
                        syncTransactionsAction({
                            fromDate: fromDate,
                            toDate: toDate,
                        })
                    );
                } catch (error) {
                    // If any action fails, ensure sync loading is set to false
                    dispatch(setSyncLoading(false));
                    throw error;
                }
            } else {
                toastText('Please select a date range to sync', 'error');
            }
            setLastSyncTime(new Date());
        } catch (error) {
            console.error('Sync error:', error);
            // Ensure sync loading is set to false on error
            dispatch(setSyncLoading(false));
        }
    };

    // Fetch transactions when filters change
    useEffect(() => {
        if (!selectedCompanyDetails) return;
        if (dateRange && dateRange[0] && dateRange[1]) {
            // Reset transactions and fetch first page when filters change
            dispatch(resetTransactions());
            fetchTransactions(1, currentSort.sortBy, currentSort.sortOrder);
        }
    }, [selectedCompanyDetails, dateRange, selectedAccounts, selectedCustomers, selectedClasses, selectedLocations, selectedEntityTypes, selectedLineBasedOn, selectedVendors]);
    // Handle batch edit
    const handleBatchEdit = () => {
        if (isHeaderChecked) {
            toastText(
                'If there are any item-based rows in the selection, account updates will be skipped for them.',
                'info',
                7000
            );
        } else {
            const itemBasedCount = getSelectedItemBasedCount(data, selectedRowKeys);
            if (itemBasedCount > 0) {
                toastText(
                    `${itemBasedCount} selected ${itemBasedCount === 1 ? 'row is' : 'rows are'} item-based. Account updates will be skipped for ${itemBasedCount === 1 ? 'it' : 'them'}.`,
                    'info',
                    7000
                );
            }
        }
        setBatchEditModalVisible(true);
    };

    // Handle batch edit modal close
    const handleBatchEditModalClose = () => {
        setBatchEditModalVisible(false);
    };

    const handleHistoryModalClose = () => {
        setHistoryModalVisible(false);
    };

    // Handle batch update
    const handleBatchUpdate = async (values: any) => {

        try {
            // Prepare the batch edit parameters with both name and value
            const batchEditParams: any = {
                fromDate: dateRange?.[0]?.format('YYYY-MM-DD'),
                toDate: dateRange?.[1]?.format('YYYY-MM-DD'),
            };

            // Add account reference with both name and value
            if (values.account) {
                batchEditParams.accountRef = {
                    value: values.account,
                    name: getOptionLabel(values.account, accountOptions)
                };
            }

            // Add customer reference with both name and value
            if (values.customer) {
                batchEditParams.customerRef = {
                    value: values.customer,
                    name: getOptionLabel(values.customer, customerOptions),
                    isProject: getOptionIsProject(values.customer, customerOptions),
                };
            }

            // Add class reference with both name and value
            if (values.class) {
                batchEditParams.classRef = {
                    value: values.class,
                    name: getOptionLabel(values.class, classOptions)
                };
            }

            // Add location reference with both name and value
            if (values.location) {
                batchEditParams.locationRef = {
                    value: values.location,
                    name: getOptionLabel(values.location, locationOptions)
                };
            }

            if (isHeaderChecked) {
                // If header checkbox is checked, edit all transactions in the date range
                batchEditParams.editAll = true;
                batchEditParams.txnLineIds = [];
                // Include current filter parameters when editAll is true
                batchEditParams.accountIds = selectedAccounts;
                batchEditParams.customerIds = selectedCustomers;
                batchEditParams.classIds = selectedClasses;
                batchEditParams.locationIds = selectedLocations;
                batchEditParams.entityTypes = selectedEntityTypes;
                batchEditParams.lineBasedOn = selectedLineBasedOn;
            } else {
                // If not checked, use selected rows with entity type information
                batchEditParams.editAll = false;
                batchEditParams.txnLineIds = groupSelectedRowsByTxnId(selectedRowKeys);
            }

            // Dispatch the batch edit action and check the result
            const result = await dispatch(batchEditTransactionsAction(batchEditParams));

            // Only close the modal if the action was fulfilled (successful)
            if (batchEditTransactionsAction.fulfilled.match(result)) {
                setBatchEditModalVisible(false);
                // // Clear all selected transactions after successful update
                // setSelectedRowKeys([]);
                // setIsHeaderChecked(false);
            }
            // If the action was rejected, the modal will remain open and error toast will be shown by Redux
        } catch (error) {
            console.error('Batch edit error:', error);
            // Modal remains open on error
        }
    };

    // Handle download attachments
    const handleDownloadAttachments = () => {
        console.log(
            'Download attachments clicked for',
            selectedRowKeys.length,
            'items'
        );
        setDownloadModalVisible(true);
        // TODO: Implement download attachments functionality
    };

    // Handle navigation to settings drawer expense groups
    const handleNavigateToExpenseGroups = () => {
        // Set the settings selection to Expense Groups
        localStorage.setItem('settings', 'Expense Groups');
        // Navigate to the current page with drawer parameters
        navigate(`${window.location.pathname}?openDrawer=true&drawerSelection=Expense Groups`);
    };


    // Capture filters to Redux when in One-Off mode
    useEffect(() => {
        if (isOneOffMode) {
            // Save filters to Redux oneOffConfigurations
            dispatch(
                updateOneOffConfigurations({
                    accountIds: selectedAccounts,
                    customerIds: selectedCustomers,
                    classIds: selectedClasses,
                    locationIds: selectedLocations,
                    vendorIds: selectedVendors,
                })
            );
        }
    }, [
        isOneOffMode,
        selectedAccounts,
        selectedCustomers,
        selectedClasses,
        selectedLocations,
        selectedVendors,
        dispatch,
    ]);

    // Rehydrate filters from Redux when returning to Tab 1 in One-Off mode
    useEffect(() => {
        if (isOneOffMode && oneOffConfigurations) {
            // Use JSON comparison to prevent infinite loops
            // Create copies before sorting to avoid mutating read-only arrays
            const currentFilters = JSON.stringify({
                accountIds: [...selectedAccounts].sort(),
                customerIds: [...selectedCustomers].sort(),
                classIds: [...selectedClasses].sort(),
                locationIds: [...selectedLocations].sort(),
                vendorIds: [...(selectedVendors || [])].sort(),
            });
            const savedFilters = JSON.stringify({
                accountIds: [...(oneOffConfigurations.accountIds || [])].sort(),
                customerIds: [...(oneOffConfigurations.customerIds || [])].sort(),
                classIds: [...(oneOffConfigurations.classIds || [])].sort(),
                locationIds: [...(oneOffConfigurations.locationIds || [])].sort(),
                vendorIds: [...(oneOffConfigurations.vendorIds || [])].sort(),
            });

            // Only update if filters are different to avoid infinite loops
            if (currentFilters !== savedFilters) {
                setSelectedAccounts(oneOffConfigurations.accountIds || []);
                setSelectedCustomers(oneOffConfigurations.customerIds || []);
                setSelectedClasses(oneOffConfigurations.classIds || []);
                setSelectedLocations(oneOffConfigurations.locationIds || []);
                setSelectedVendors(oneOffConfigurations.vendorIds || []);
            }
        }
    }, [isOneOffMode, oneOffConfigurations]);

    // Handle expense group selection
    const handleExpenseGroupChange = (expenseGroupId: string) => {
        propOnExpenseGroupChange(expenseGroupId);
        clearEntityLineFilters();

        if (expenseGroupId === 'all') {
            // Filters will be rehydrated from Redux if available
            if (oneOffConfigurations) {
                setSelectedAccounts(oneOffConfigurations.accountIds || []);
                setSelectedCustomers(oneOffConfigurations.customerIds || []);
                setSelectedClasses(oneOffConfigurations.classIds || []);
                setSelectedLocations(oneOffConfigurations.locationIds || []);
                setSelectedVendors(oneOffConfigurations?.vendorIds || []);
            } else {
                setSelectedAccounts([]);
                setSelectedCustomers([]);
                setSelectedClasses([]);
                setSelectedLocations([]);
                setSelectedVendors([]);
            }
        } else {
            // Find the selected expense group and populate the dropdowns
            const selectedGroup = expenseGroups?.find((group: any) => group.id === expenseGroupId);
            if (selectedGroup) {
                setSelectedAccounts(selectedGroup.accounts || []);
                setSelectedCustomers(selectedGroup.customers || []);
                setSelectedClasses(selectedGroup.classes || []);
                setSelectedLocations(selectedGroup.locations || []);
                setSelectedVendors(selectedGroup.vendors || []);
            }
        }
    };


    useEffect(() => {
        if (expenseGroups && expenseGroups.length > 0 && propSelectedExpenseGroup) {
            handleExpenseGroupChange(propSelectedExpenseGroup);
        }
        clearEntityLineFilters();
    }, []);

    // Expense group dropdown menu
    const expenseGroupMenu = (
        <div className="expense-group-dropdown-container">
            <Menu
                className="expense-group-dropdown-menu"
                selectedKeys={[propSelectedExpenseGroup]}
                onClick={({ key }) => {
                    if (key === 'manage-expense-groups') {
                        handleNavigateToExpenseGroups();
                    } else {
                        handleExpenseGroupChange(key);
                    }
                }}
            >
                {propExpenseGroupOptions.map(option => (
                    <Menu.Item key={option.value} className="expense-group-menu-item">
                        <span className="expense-group-menu-item-text" title={option.label}>
                            {option.label}
                        </span>
                    </Menu.Item>
                ))}
                {isExpenseGroupsAddPermission && (
                    <>
                        <Menu.Divider />
                        <Menu.Item
                            key="manage-expense-groups"
                            className="expense-group-menu-item expense-group-sticky-item"
                            style={{
                                padding: 0,
                                margin: 0,
                            }}
                        >
                            <div style={{ padding: '0px 10px', display: 'flex', justifyContent: 'flex-start' }}>
                                <Button
                                    className="btn-black"
                                    style={{
                                        fontSize: '12px',
                                        width: 'auto',
                                        minWidth: '100px',
                                        textAlign: 'left',
                                    }}
                                >
                                    <span style={{ fontWeight: '600', fontSize: '18px', marginRight: '0px' }}>+</span>
                                    Add Expense Group
                                </Button>
                            </div>
                        </Menu.Item>
                    </>
                )}
            </Menu>
        </div>
    );

    return (
        <div className={styles['expense-transaction-main']}>
            {/* Search and Filter Section */}
            <ExpenseTransactionSearchAndFilter
                dateRange={dateRange}
                setDateRange={setDateRange}
                selectedAccounts={selectedAccounts}
                setSelectedAccounts={setSelectedAccounts}
                selectedCustomers={selectedCustomers}
                setSelectedCustomers={setSelectedCustomers}
                selectedClasses={selectedClasses}
                setSelectedClasses={setSelectedClasses}
                selectedLocations={selectedLocations}
                setSelectedLocations={setSelectedLocations}
                selectedEntityTypes={selectedEntityTypes}
                selectedLineBasedOn={selectedLineBasedOn}
                selectedVendors={selectedVendors}
                setSelectedEntityTypes={setSelectedEntityTypes}
                setSelectedLineBasedOn={setSelectedLineBasedOn}
                setSelectedVendors={setSelectedVendors}
                isSummaryView={isSummaryView}
                setIsSummaryView={handleViewSwitch}
                accountOptions={accountOptions}
                customerOptions={customerOptions}
                classOptions={classOptions}
                locationOptions={locationOptions}
                vendorOptions={vendorOptions}
                onBatchEdit={handleBatchEdit}
                onDownloadAttachments={handleDownloadAttachments}
                lastSyncTime={lastSyncTime}
                handleSync={handleSync}
                loading={loading || propExpenseGroupsLoading || transactionsFirstTimeLoading}
                switchLoading={switchLoading}
                syncLoading={syncLoading}
                expenseGroupOptions={propExpenseGroupOptions}
                selectedExpenseGroup={propSelectedExpenseGroup}
                expenseGroupMenu={expenseGroupMenu}
                selectedCount={selectedRowKeys.length}
                expenseGroupId={propSelectedExpenseGroup}
                visibleColumnKeys={visibleColumnKeys}
                onVisibleColumnsChange={onVisibleColumnsChange}
                onSaveVisibleColumnsAsDefault={onSaveVisibleColumnsAsDefault}
            />
            {/* Warning Message */}
            {/* <div style={{ margin: '-5px 20px 10px 20px' }}>
                <span style={{ fontStyle: 'italic', fontSize: '12px', color: '#666' }}>
                    Note: Item-based transactions are not included in this allocation view.
                </span>
            </div> */}

            {/* Table Section */}
            {!isSummaryView ? (
                <ExpenseTransactionTable
                    data={data}
                    loading={loading || transactionsFirstTimeLoading}
                    onSort={handleSort}
                    currentSort={currentSort}
                    hasMore={hasMore}
                    isLoadingMore={isLoadingMore}
                    onLoadMore={handleLoadMore}
                    selectedRowKeys={selectedRowKeys}
                    isHeaderChecked={isHeaderChecked}
                    onHeaderCheckboxChange={handleHeaderCheckboxChange}
                    onRowCheckboxChange={handleRowCheckboxChange}
                    grandTotal={transactionGrandTotal}
                    accountOptions={accountOptions}
                    customerOptions={customerOptions}
                    classOptions={classOptions}
                    locationOptions={locationOptions}
                    dateRange={dateRange ?? undefined}
                    selectedAccounts={selectedAccounts}
                    selectedCustomers={selectedCustomers}
                    selectedClasses={selectedClasses}
                    selectedLocations={selectedLocations}
                    selectedEntityTypes={selectedEntityTypes}
                    selectedLineBasedOn={selectedLineBasedOn}
                    onEntityTypeFilterChange={setSelectedEntityTypes}
                    onLineBasedOnFilterChange={setSelectedLineBasedOn}
                    visibleColumnKeys={visibleColumnKeys}
                />
            ) : (
                <SummaryTable
                    data={summaryData}
                    grandTotal={grandTotal}
                    loading={loading || summaryLoading || summaryFirstTimeLoading}
                    onSort={handleSummarySort}
                    currentSort={currentSummarySort}
                    hasMore={summaryHasMore}
                    isLoadingMore={summaryIsLoadingMore}
                    onLoadMore={handleLoadMoreSummary}
                    selectedAccounts={selectedAccounts}
                    selectedCustomers={selectedCustomers}
                    selectedClasses={selectedClasses}
                    selectedLocations={selectedLocations}
                />
            )}

            {/* Batch Edit Modal */}
            <BatchEditModal
                visible={batchEditModalVisible}
                onCancel={handleBatchEditModalClose}
                onUpdate={handleBatchUpdate}
                accountOptions={accountOptions}
                customerOptions={customerOptions}
                classOptions={classOptions}
                locationOptions={locationOptions}
                isLoading={batchEdit?.isLoading || false}
            />

            <DownloadAttachmentModal
                visible={downloadModalVisible}
                onCancel={handleDownloadModalClose}
                selectedCount={selectedRowKeys.length}
                selectedTxnIds={selectedTxnIds}
                selectedTransactions={data.filter(transaction =>
                    selectedTxnIds.includes(transaction.txnId)
                )}
                isHeaderChecked={isHeaderChecked}
                dateRange={dateRange}
                selectedAccounts={selectedAccounts}
                selectedCustomers={selectedCustomers}
                selectedClasses={selectedClasses}
                selectedLocations={selectedLocations}
                selectedEntityTypes={selectedEntityTypes}
                selectedLineBasedOn={selectedLineBasedOn}
                selectedVendors={selectedVendors}
            />
            <HistoryModal
                visible={historyModalVisible}
                onCancel={handleHistoryModalClose}
                defaultTab="all"
            />
        </div>
    );
};

export default ExpenseTransactionMain;
